import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api, resolveImage } from "@/lib/api";
import { Play, ExternalLink, Instagram, Youtube } from "lucide-react";

export default function Gallery() {
  const { lang, t, pick } = useLang();
  const { settings } = useSettings();
  const [tab, setTab] = useState("photos");
  const [cat, setCat] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    const kind = tab === "photos" ? "photo" : "video";
    api
      .get("/media", { params: { kind } })
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, [tab]);

  const filtered = useMemo(
    () => (cat === "all" ? items : items.filter((i) => i.category === cat)),
    [items, cat]
  );

  const cats = [
    { key: "all", label: t("gallery.filters.all") },
    { key: "carpentry", label: t("gallery.filters.carpentry") },
    { key: "construction", label: t("gallery.filters.construction") },
    { key: "consultation", label: t("gallery.filters.consultation") },
  ];

  return (
    <div data-testid="page-gallery" className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24 pb-32">
      <section className="pt-20 pb-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-8">
          <p className="text-eyebrow text-brand mb-6">{t("gallery.eyebrow")}</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl text-ink leading-[0.95] tracking-tighter">
            {t("gallery.title")}
          </h1>
        </div>
        <div className="md:col-span-4 text-ink-muted leading-relaxed">
          {t("gallery.subtitle")}
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-4 border-y border-edge py-5 mb-12">
        <div className="flex items-center gap-2">
          {[
            { k: "photos", label: t("gallery.tabs.photos") },
            { k: "videos", label: t("gallery.tabs.videos") },
          ].map((it) => (
            <button
              key={it.k}
              data-testid={`gallery-tab-${it.k}`}
              onClick={() => {
                setTab(it.k);
                setCat("all");
              }}
              className={`px-5 py-2 text-eyebrow transition-colors ${
                tab === it.k
                  ? "text-ink-inverse bg-brand"
                  : "text-ink-muted hover:text-brand"
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`gallery-filter-${c.key}`}
              onClick={() => setCat(c.key)}
              className={`px-3 py-1.5 text-eyebrow transition-colors ${
                cat === c.key
                  ? "text-brand border-b border-brand"
                  : "text-ink-muted hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-ink-muted text-center py-20">…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" data-testid="gallery-empty">
          <p className="text-ink-muted mb-8">{t("gallery.empty")}</p>
          <div className="flex justify-center gap-4">
            {settings.youtube_url && (
              <a
                data-testid="gallery-youtube-cta"
                href={settings.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-ink-inverse text-sm hover:bg-brand-hover transition-colors"
              >
                <Youtube size={16} /> {t("gallery.youtubeCta")}
              </a>
            )}
            {settings.instagram_url && (
              <a
                data-testid="gallery-instagram-cta"
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border border-edge text-ink hover:border-brand hover:text-brand text-sm transition-colors"
              >
                <Instagram size={16} /> {t("gallery.instagramCta")}
              </a>
            )}
          </div>
        </div>
      ) : tab === "photos" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {filtered.map((m) => (
            <button
              key={m.id}
              data-testid={`gallery-photo-${m.id}`}
              onClick={() => setLightbox(m)}
              className="group relative aspect-[4/5] overflow-hidden border border-edge hover:border-brand transition-colors"
            >
              <img
                src={resolveImage(m.image_url)}
                alt={pick(m, "title")}
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#131211] to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-ink text-sm">{pick(m, "title")}</p>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((m) => (
            <article
              key={m.id}
              data-testid={`gallery-video-${m.id}`}
              className="group bg-bg-surface border border-edge hover:border-brand transition-colors"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={
                    m.image_url
                      ? resolveImage(m.image_url)
                      : `https://i.ytimg.com/vi/${m.youtube_id}/hqdefault.jpg`
                  }
                  alt={pick(m, "title")}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <a
                  href={`https://www.youtube.com/watch?v=${m.youtube_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`gallery-video-play-${m.id}`}
                  className="absolute inset-0 flex items-center justify-center bg-[#131211]/40 group-hover:bg-[#131211]/20 transition-colors"
                  aria-label="Play"
                >
                  <span className="w-16 h-16 bg-brand text-ink-inverse flex items-center justify-center">
                    <Play size={26} />
                  </span>
                </a>
              </div>
              <div className="p-5">
                <p className="text-eyebrow text-brand mb-2">{m.category}</p>
                <h3 className="font-display text-xl text-ink leading-tight">
                  {pick(m, "title")}
                </h3>
                {settings.youtube_url && (
                  <a
                    href={`https://www.youtube.com/watch?v=${m.youtube_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-eyebrow text-ink-muted hover:text-brand transition-colors"
                  >
                    <ExternalLink size={14} /> {lang === "en" ? "Open" : "Abrir"}
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-[#0a0a0a]/95 flex items-center justify-center p-6 animate-fade-in"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-6 right-6 text-ink-muted hover:text-brand text-eyebrow"
            onClick={() => setLightbox(null)}
          >
            CLOSE
          </button>
          <img
            src={resolveImage(lightbox.image_url)}
            alt=""
            className="max-h-[88vh] max-w-[90vw] object-contain"
          />
        </div>
      )}
    </div>
  );
}
